import { useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useAuthContext } from '../../hooks/useAuthContext';
import { apiFetch } from '../../utils/api';
import { normalizeTranslation } from '../../utils/translationUtils';
import ItemEditor from '../../components/ItemEditor/ItemEditor';
import Spinner from '../../components/Spinner/Spinner';
import Breadcrumb from '../../components/Breadcrumb/Breadcrumb';
import BackLink from '../../components/BackLink/BackLink';
import './AdminItemPage.css';

const normalizeBullet = (bullet, type) => {
  if (type === 'keyvalue') {
    return {
      key: normalizeTranslation(bullet.key),
      value: normalizeTranslation(bullet.value),
    };
  }
  return normalizeTranslation(bullet);
};

const normalizeItem = (raw) => ({
  image: raw.image ?? '',
  imageFileId: raw.imageFileId ?? null,
  featured: raw.featured ?? false,
  name: normalizeTranslation(raw.name),
  description: normalizeTranslation(raw.description),
  sections: (raw.sections ?? []).map(s => ({
    name: normalizeTranslation(s.name),
    type: s.type,
    display: s.display ?? 'list',
    bullets: (s.bullets ?? []).map(b => normalizeBullet(b, s.type)),
  })),
});

const AdminItemPage = () => {
  const { id: categoryId, itemId } = useParams();
  const { user } = useAuthContext();
  const [item, setItem] = useState(null);
  const [initialCategories, setInitialCategories] = useState([]);
  const [category, setCategory] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const [itemRes, catRes] = await Promise.all([
        apiFetch(`/api/admin/categories/${categoryId}/items/${itemId}`, {
          headers: { Authorization: `Bearer ${user.token}` },
        }),
        apiFetch(`/api/admin/categories/${categoryId}`, {
          headers: { Authorization: `Bearer ${user.token}` },
        }),
      ]);
      const itemData = await itemRes.json();
      const catData = await catRes.json();
      setItem(normalizeItem(itemData));
      setInitialCategories(itemData.categories ?? []);
      setCategory(catData);
      setLoading(false);
    };
    load();
  }, [categoryId, itemId, user.token]);

  if (loading) return <Spinner />;
  if (!item) return <div>Item not found.</div>;

  const categoryName = category?.translations?.en ?? 'Category';
  const itemName = item.name?.en ?? 'Item';

  return (
    <div className="admin-item-page">
      <Breadcrumb items={[
        { label: 'Categories', to: '/categories' },
        { label: categoryName, to: `/categories/${categoryId}` },
        { label: itemName },
      ]} />

      <BackLink to={`/categories/${categoryId}`} label={`Back to ${categoryName}`} />

      <h2 className="admin-item-title">{itemName}</h2>

      <div className="admin-item-editor-wrap">
        <ItemEditor
          categoryId={categoryId}
          itemId={itemId}
          initialItem={item}
          initialCategories={initialCategories}
        />
      </div>
    </div>
  );
};

export default AdminItemPage;
